import type {
	CompositeFilterDescriptor,
	SortDescriptor,
} from "@tql/ui-components-react/kendo/data-query";
import type {
	GridFilterChangeEvent,
	GridSortChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useState } from "react";

import { pruneEmptyFilters } from "~/shared/utils/prune-empty-filters.ts";

import {
	initialGridFilter,
	initialGridSort,
} from "../constants/grid-defaults.ts";
import { cardTypeToStatusFilter } from "../constants/shipment-status-filter.ts";
import type { FilterMappingKey } from "../utils/filter-mappings.ts";
import { filterMappings } from "../utils/filter-mappings.ts";
import { readIdsFromUrl } from "../utils/read-ids-from-url.ts";

export type ShipmentFilter = {
	status: number | null;
	needsImmediateAction: boolean;
	needsAcknowledgment: boolean;
};

export const useShipmentGridState = () => {
	const [gridFilter, setGridFilter] =
		useState<CompositeFilterDescriptor>(initialGridFilter);
	const [gridSort, setGridSort] = useState<SortDescriptor[]>(initialGridSort);
	const [shipmentFilter, setShipmentFilter] = useState<ShipmentFilter>({
		status: null,
		needsImmediateAction: false,
		needsAcknowledgment: false,
	});
	const [activeCardType, setActiveCardType] = useState<string | null>(null);
	const [ltlBrokerIds, setLtlBrokerIds] = useState<number[]>(() =>
		readIdsFromUrl("ltlBrokerIds"),
	);
	const [primaryBrokerIds, setPrimaryBrokerIds] = useState<number[]>(() =>
		readIdsFromUrl("primaryBrokerIds"),
	);

	const handleGridFilterChange = useCallback((e: GridFilterChangeEvent) => {
		// Kendo's default text filter cell leaves its descriptor in the
		// composite tree with an empty `value` when the user clears the
		// input rather than removing the descriptor. Left as-is, the
		// subsequent `process()` pass evaluates `contains("")` against
		// the column, which excludes rows whose field is null/undefined
		// and makes it look like the original data is gone. Prune any
		// empty leaves so a cleared filter behaves like "no filter".
		setGridFilter(pruneEmptyFilters(e.filter) || initialGridFilter);
	}, []);

	const handleGridSortChange = useCallback((e: GridSortChangeEvent) => {
		setGridSort(e.sort);
	}, []);

	const onClickSummaryCard = useCallback(
		(cardType: string, needsImmediateAction: boolean) => {
			// Clicking the currently-active card toggles its filter off and
			// restores the default (landing-page) view. Compare both cardType
			// and needsImmediateAction so a regular status card and an
			// exception indicator that share a cardType stay distinct.
			const isSameClick =
				activeCardType === cardType &&
				shipmentFilter.needsImmediateAction === needsImmediateAction;
			if (isSameClick) {
				setGridFilter(initialGridFilter);
				setActiveCardType(null);
				setShipmentFilter((prev) => ({
					...prev,
					status: null,
					needsImmediateAction: false,
				}));
				return;
			}
			setGridFilter(initialGridFilter);
			setActiveCardType(cardType);
			setShipmentFilter((prev) => ({
				...prev,
				status: cardTypeToStatusFilter[cardType] ?? null,
				needsImmediateAction,
				needsAcknowledgment: needsImmediateAction
					? false
					: prev.needsAcknowledgment,
			}));
		},
		[activeCardType, shipmentFilter.needsImmediateAction],
	);

	const resetBoardFilters = useCallback(() => {
		setGridFilter(initialGridFilter);
		setLtlBrokerIds([]);
		setPrimaryBrokerIds([]);
		setShipmentFilter({
			status: null,
			needsImmediateAction: false,
			needsAcknowledgment: false,
		});
	}, []);

	const setAcknowledgmentFilter = useCallback((value: boolean) => {
		setGridFilter(initialGridFilter);
		setShipmentFilter((prev) => ({
			...prev,
			status: null,
			needsImmediateAction: false,
			needsAcknowledgment: value,
		}));
	}, []);

	// Apply filter from URL search param on mount
	useEffect(() => {
		const searchParams = new URLSearchParams(globalThis.location.search);
		const filterParam = searchParams.get("filter") as FilterMappingKey;

		if (filterParam && filterMappings[filterParam]) {
			setShipmentFilter(filterMappings[filterParam]);
		}
	}, []);

	return {
		gridFilter,
		gridSort,
		shipmentFilter,
		activeCardType,
		ltlBrokerIds,
		primaryBrokerIds,
		handleGridFilterChange,
		handleGridSortChange,
		onClickSummaryCard,
		resetBoardFilters,
		setAcknowledgmentFilter,
	};
};
